import { FlexColumn, FlexRow, LoaderBar, PageHeader } from 'components'
import styled from 'styled-components'
import { GetPublicInspection, useQueryString } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { Accordion, AccordionContent, AccordionTitle, Button, Card, CardContent, Checkbox, Form, Label, Radio, TextArea } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { DesktopMode, MobileMode, useTabletAndBelowMediaQuery } from 'layout'
import {
    ControlType,
    DateFormat,
    DateUtility,
    TemplateQuestionResponseType,
    TemplateResponseType,
} from 'utility'
import { useResponses } from 'context'
import { FileUploadPicker } from 'components/FileUploadPicker'
import { CustomePreview } from 'page-components/inspection/Elements'
import ReactSignatureCanvas from 'react-signature-canvas'

const InspectionListContainer = styled.div`
    .w-100 {
        width: 100% !important;
    }
    .sign-border {
        border: solid 1px;
    }
    .section-name {
        color: ${({ theme }) => theme.colors.white} !important;
        background-color: ${({ theme }) => theme.colors.primary} !important;
    }
    .accordion {
        overflow: hidden;
        box-shadow: none !important;
        .outer-box {
            border: solid 0.5px;
            border-top: 0px;
            border-bottom-left-radius: 5px;
            border-bottom-right-radius: 5px;
        }
    }
`

const QuestionBox = styled.div`
    .notes {
        border-left: solid 1px;
        height: 100%;
        .form {
            width: 100%;
            height: 100%;
            textarea {
                height: 80%;
            }
        }
    }
    .error {
        color: #9f3a38 !important;
        input {
            background-color: #fff6f6 !important;
            border-color: #e0b4b4 !important;
            color: #9f3a38 !important;
        }
    }
`

const NotesMediaContainer = styled(Form)`
    .active {
        background-color: ${({ theme }) => theme.colors.primary} !important;
        label {
            color: ${({ theme }) => theme.colors.white};
        }
    }
    .preview-media {
        height: 100px;
        width: 100px;
    }
`

const NoteMediaActions = ({ item }) => {
    const [note, setNote] = useState('')
    const [IsInternal, setIsInternal] = useState(false)
    const [files, setFiles] = useState([])
    useEffect(() => {
        if (IsInternal) {
            setNote(item?.Notes.find(ele => ele.IsInternal)?.Notes || '')
        } else {
            setNote(item?.Notes.find(ele => !ele.IsInternal)?.Notes || '')
        }
    }, [item?.Notes, IsInternal])

    useEffect(() => {
        const filesList = item?.Answers?.MediaResponse?.length
            ? item?.Answers?.MediaResponse
            : item.Files
        if (filesList?.length) {
            setFiles(
                filesList.map((ele, i) => ({
                    ...ele,
                    id: ele.MediaId,
                    name: ele.FileKey || `File ${i}`,
                    url: ele.FileURL,
                })),
            )
        }
    }, [item])

    const toggleNote = () => {
        setIsInternal(!IsInternal)
    }

    return (
        <NotesMediaContainer>
            <DesktopMode>
                <FlexRow className=" ">
                    <div className="px-2 mb-1 col-6 d-flex  justify-content-start align-items-start">
                        <FileUploadPicker
                            label="Media"
                            maxFileSize={500}
                            files={files}
                            minHeight="100px"
                            previewItem={<CustomePreview item={item} />}
                            previewItemClassName="preview-media"
                            customePreview
                            onlyPreview
                        />
                    </div>
                    <div className="px-2 mb-1 col-6 d-flex   align-items-start align-self-start">
                        <Form.Field className="mb-3 col-12">
                            <Label className="p-0 mb-2">
                                <Label
                                    onClick={toggleNote}
                                    className={`${!IsInternal && 'active'}`}
                                >
                                    <label>Notes</label>
                                </Label>
                                <Label
                                    onClick={toggleNote}
                                    className={`${IsInternal && 'active'}`}
                                >
                                    <label>Internal Notes</label>
                                </Label>
                            </Label>
                            <TextArea value={note} readonly />
                        </Form.Field>
                    </div>
                </FlexRow>
            </DesktopMode>
            <MobileMode>
                <FlexColumn className="  mt-2">
                    <div className="mb-1 col-12 px-0 d-flex  justify-content-start align-items-start">
                        <FileUploadPicker
                            maxFiles={5}
                            label="Media"
                            files={files}
                            previewItem={<CustomePreview item={item} />}
                        />
                    </div>
                    <div className="mb-1 col-12 px-0 d-flex   align-items-start">
                        <Form.Field className="mb-3 col-12">
                            <Label className="p-0 mb-2">
                                <Label
                                    onClick={toggleNote}
                                    className={`${!IsInternal && 'active'}`}
                                >
                                    <label>Notes</label>
                                </Label>
                                <Label
                                    onClick={toggleNote}
                                    className={`${IsInternal && 'active'}`}
                                >
                                    <label>Internal Notes</label>
                                </Label>
                            </Label>
                            <TextArea value={note} readonly />
                        </Form.Field>
                    </div>
                </FlexColumn>
            </MobileMode>
        </NotesMediaContainer>
    )
}

const SingleResponseBox = ({ item, responses = [] }) => {
    const reponseType = useMemo(() => {
        return responses?.find(ele => ele.Id === item?.QuestionResponseId)
    }, [item, responses])

    const [value, setValue] = useState()

    useEffect(() => {
        if (
            [
                ControlType.DateAndTime,
                ControlType.Date,
                ControlType.Time,
            ].includes(reponseType?.Id)
        ) {
            if (item?.Answers?.SingleResponse?.Response) {
                setValue(
                    DateUtility.dateToLocalISO(
                        new Date(item?.Answers?.SingleResponse?.Response),
                    ),
                )
            }
        } else {
            setValue(item?.Answers?.SingleResponse?.Response)
        }
    }, [item])

    return <div>{value}</div>
}

const MultipleResponseBox = ({ item, answers, onAnswer }) => {
    const responseType = useMemo(
        () =>
            answers.MultipleResponse?.length ? answers.MultipleResponse : [],
        [item, answers],
    )
    const isMobile = useTabletAndBelowMediaQuery()

    const options = useMemo(
        () =>
            responseType?.map(ele => ({
                value: ele.Id,
                text: ele.DisplayText,
            })),
        [responseType],
    )

    const value = useMemo(
        () => responseType?.filter(ele => ele.IsSelected)?.map(ele => ele.Id),
        [responseType],
    )

    const Score = useMemo(
        () =>
            answers.MultipleResponse?.length
                ? answers.MultipleResponse?.find(
                      ele => ele.IsScore && ele.IsSelected,
                  )
                : {},
        [answers.MultipleResponse],
    )

    const onAnswerSelect = (_, data) => {
        if (!data.value.length) {
            onAnswer([])
            return
        }
        onAnswer(data.value)
    }

    return (
        <div>
            {item.MultiResponseType === TemplateQuestionResponseType.Single ? (
                <>
                    {responseType?.map(ele => {
                        return (
                            <div
                                key={ele.Id}
                                onClick={() => onAnswer(ele)}
                                className="d-flex align-items-center mb-3"
                            >
                                {TemplateQuestionResponseType.Single ===
                                    ele.MultiResponseType &&
                                item.MultiResponseType ===
                                    TemplateQuestionResponseType.Single ? (
                                    <Radio
                                        checked={ele.IsSelected}
                                        name={item.Id}
                                        disabled
                                    />
                                ) : (
                                    <Checkbox
                                        disabled
                                        checked={ele.IsSelected}
                                    />
                                )}
                                <span className="ms-3">{ele.DisplayText}</span>
                            </div>
                        )
                    })}
                </>
            ) : (
                <>
                    <Dropdown
                        placeholder="Select"
                        options={options}
                        onChange={onAnswerSelect}
                        value={value}
                        className={isMobile ? 'col-12' : 'col-7'}
                        multiple
                        selection
                        search
                        disabled
                    />
                </>
            )}
            {Boolean(Score?.Score) && <Label>Score: {Score?.Score}</Label>}
        </div>
    )
}

const ResponseBox = ({ question }) => {
    const { SingleResponses, MultipleResponse } = useResponses()

    return (
        <div className="mt-2">
            {TemplateResponseType.Single === question.TypeOfResponse && (
                <SingleResponseBox
                    responses={SingleResponses}
                    item={question}
                />
            )}
            {TemplateResponseType.Multiple === question.TypeOfResponse && (
                <MultipleResponseBox
                    item={question}
                    responses={MultipleResponse}
                    answers={question.Answers}
                />
            )}
        </div>
    )
}

const QuetionBox = ({ item }) => {
    const isMobile = useTabletAndBelowMediaQuery()

    return (
        <>
            <Card className="w-100 mb-4 p-3">
                <QuestionBox>
                    <FlexRow className={`${isMobile ? 'flex-column' : ''}`}>
                        <FlexColumn
                            className={`${isMobile ? 'col-12' : 'col-5'}`}
                        >
                            <div
                                dangerouslySetInnerHTML={{ __html: item.FAQ }}
                            />
                            <ResponseBox question={item} />
                        </FlexColumn>
                        <div className={`${isMobile ? 'col-12' : 'col-7'}`}>
                            <NoteMediaActions item={item} />
                        </div>
                    </FlexRow>
                </QuestionBox>
            </Card>
        </>
    )
}

const SectionBox = ({ item }) => {
    const [open, setOpen] = useState(true)
    const isMobile = useTabletAndBelowMediaQuery()
    return (
        <Accordion fluid styled className="mt-4">
            <AccordionTitle
                active={open}
                index={item.SectionId}
                onClick={() => setOpen(!open)}
                className="section-name"
            >
                {item.SectionName}
            </AccordionTitle>
            <AccordionContent className="outer-box" active={open}>
                <CardContent className={`${isMobile && 'px-0'} row`}>
                    {item.Questions.map(ele => (
                        <div
                            key={ele.Id}
                            className={`${isMobile && 'px-0'} col-12`}
                        >
                            <QuetionBox item={ele} />
                        </div>
                    ))}
                </CardContent>
            </AccordionContent>
        </Accordion>
    )
}

export const CapaScreen = () => {
    const params = useQueryString()

    const reportId = params.get('reportId')
    const otp = params.get('otp')

    let sigPad = {}

    const clear = () => {
        sigPad.clear()
    }

    const trim = () => {
        console.log(sigPad.getTrimmedCanvas().toDataURL('image/png'))
    }

    const { loading, data } = GetPublicInspection(reportId, otp)
    const list = useMemo(() => data?.Sections || [], [data])

    const totalScore = useMemo(() => {
        let total = 0
        if (list.length) {
            list.forEach(section => {
                if (section.Questions.length) {
                    section.Questions.forEach(question => {
                        total += question.Score || 0
                    })
                }
            })
        }

        return total
    }, [list])

    return (
        <div className="container mt-5">
            {loading && <LoaderBar />}
            <div className="mb-4 px-2">
                <PageHeader header={data?.AuditName} />
                <div>
                    {data?.CreatedOn && (
                        <p>
                            Date Created:{' '}
                            {DateUtility.dateToLocalISO(
                                new Date(data?.CreatedOn),
                                DateFormat.date,
                            )}
                        </p>
                    )}
                    <p>Created By: {data?.CreatedByName || ''}</p>
                    <p>Location Name: {data?.LocationName || ''}</p>
                    <p>Score: {totalScore}</p>
                </div>
            </div>
            <InspectionListContainer className="row mx-0 mb-5">
                {list.map(ele => (
                    <div key={ele.SectionId} className="col-12">
                        <SectionBox item={ele} />
                    </div>
                ))}
                <div className="col-12 my-5">
                    <div
                        style={{
                            border: '1px solid #e2e2e2',
                            width: 500,
                            height: 200,
                        }}
                    >
                        <ReactSignatureCanvas
                            canvasProps={{
                                width: 500,
                                height: 200,
                            }}
                            ref={ref => {
                                sigPad = ref
                            }}
                        />
                    </div>
                    <FlexRow>
                        <Button type="button" onClick={trim} className="my-2">
                            Sign
                        </Button>
                        <Button type="button" onClick={clear} className="my-2">
                            Clear
                        </Button>
                    </FlexRow>
                </div>
            </InspectionListContainer>
        </div>
    )
}
